<script setup lang="ts">
import type { BaseButtonProps, BaseButtonTag, BaseButtonType } from "./types";

defineOptions({
  name: "BaseButton",
  inheritAttrs: false,
});

const props = withDefaults(defineProps<BaseButtonProps>(), {
  type: "button",
  inactive: false,
});

const attrs = useAttrs();
const hasWarnedDisabled = shallowRef(false);

const isDisabledAttr = computed(() => attrs.disabled === "" || attrs.disabled === true || attrs.disabled === "true");

const isNonInteractive = computed(() => props.inactive || isDisabledAttr.value);

const tag = computed<BaseButtonTag>(() => {
  if (props.type === "router-link" || props.type === "a") {
    return props.type;
  }

  if (attrs.to) {
    return "router-link";
  }

  if (attrs.href) {
    return "a";
  }

  return "button";
});

const buttonType = computed<Extract<BaseButtonType, "button" | "submit" | "reset">>(() => {
  if (props.type === "submit" || props.type === "reset") {
    return props.type;
  }

  return "button";
});

const forwardedAttrs = computed<Record<string, unknown>>(() => {
  const nextAttrs = { ...attrs };

  delete nextAttrs.disabled;

  if (tag.value === "button") {
    delete nextAttrs.href;
    delete nextAttrs.to;
  }

  if (tag.value === "a") {
    delete nextAttrs.to;
  }

  if (tag.value === "router-link") {
    delete nextAttrs.href;
  }

  return nextAttrs;
});

const componentProps = computed<Record<string, unknown>>(() => {
  const ariaDisabled = isNonInteractive.value ? "true" : "false";
  const tabindex = isNonInteractive.value ? -1 : undefined;

  if (tag.value === "button") {
    return {
      type: buttonType.value,
      disabled: isDisabledAttr.value || undefined,
      "aria-disabled": ariaDisabled,
    };
  }

  if (tag.value === "a") {
    return {
      href: isNonInteractive.value ? undefined : (attrs.href ?? attrs.to),
      tabindex,
      "aria-disabled": ariaDisabled,
    };
  }

  return {
    to: attrs.to,
    tabindex,
    "aria-disabled": ariaDisabled,
  };
});

const resolvedAttrs = computed<Record<string, unknown>>(() => ({
  ...forwardedAttrs.value,
  ...componentProps.value,
}));

watchEffect(() => {
  if (!hasWarnedDisabled.value && isDisabledAttr.value) {
    devConsole.warn("Avoid using disabled on BaseButton. Use inactive instead.");
    hasWarnedDisabled.value = true;
  }
});

function suppressNonInteractiveActivation(event: Event) {
  if (!isNonInteractive.value) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if ("stopImmediatePropagation" in event && typeof event.stopImmediatePropagation === "function") {
    event.stopImmediatePropagation();
  }
}
</script>
<template>
  <component
    :is="tag"
    v-bind="resolvedAttrs"
    class="base-button"
    @click.capture="suppressNonInteractiveActivation"
    @keydown.enter.capture="suppressNonInteractiveActivation"
    @keydown.space.capture="suppressNonInteractiveActivation"
  >
    <span class="button-label">
      <slot />
    </span>
    <span v-if="$slots.icon || props.icon" class="button-icon">
      <slot name="icon">
        <BaseIcon v-if="props.icon" :name="props.icon" />
      </slot>
    </span>
  </component>
</template>
<style lang="css" scoped>
.base-button {
  /** Base Properties */
  --button-margin: 0.5rem;
  --button-gap: 8px;
  --button-border: transparent;
  --button-background: transparent;
  --button-color: currentcolor;
  --button-box-shadow: none;
  --button-text-decoration: none;

  /** Hover Properties */
  --button-hover-border: var(--button-border);
  --button-hover-background: var(--button-background);
  --button-hover-color: var(--button-color);
  --button-hover-box-shadow: var(--button-box-shadow);
  --button-hover-text-decoration: var(--button-text-decoration);

  /** Focus Properties */
  --button-focus-border: var(--button-border);
  --button-focus-background: var(--button-background);
  --button-focus-color: var(--button-color);
  --button-focus-box-shadow: var(--button-box-shadow);
  --button-focus-text-decoration: var(--button-text-decoration);
  --button-focus-outline: var(--color-nblue-2);

  /** Active Properties */
  --button-active-border: var(--button-hover-border);
  --button-active-background: var(--button-hover-background);
  --button-active-color: var(--button-hover-color);
  --button-active-box-shadow: var(--button-hover-box-shadow);
  --button-active-text-decoration: var(--button-hover-text-decoration);
  --button-active-transform: none;

  /** Disabled Properties */
  --button-disabled-border: var(--button-border);
  --button-disabled-background: var(--button-background);
  --button-disabled-color: var(--button-color);
  --button-disabled-box-shadow: none;
  --button-disabled-text-decoration: var(--button-text-decoration);
  --button-disabled-transform: none;

  /** Inactive Properties */
  --button-inactive-border: var(--button-border);
  --button-inactive-background: var(--button-background);
  --button-inactive-color: var(--button-color);
  --button-inactive-box-shadow: none;
  --button-inactive-text-decoration: var(--button-text-decoration);
  --button-inactive-transform: none;

  border-width: 2px;
  border-style: solid;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: var(--button-gap);
  border-radius: 32px;
  margin: var(--button-margin);
  border-color: var(--button-border);
  background-color: var(--button-background);
  color: var(--button-color);
  box-shadow: var(--button-box-shadow);
  text-decoration: var(--button-text-decoration);
  outline: none;
  transition:
    background-color 150ms ease,
    color 150ms ease,
    border-color 150ms ease,
    box-shadow 150ms ease,
    outline-color 150ms ease,
    outline-offset 150ms ease,
    text-decoration-color 150ms ease,
    transform 150ms ease;
}

.base-button:where(:not(:disabled):not([aria-disabled="true"])):is(:hover, :focus-visible, :active) {
  border-color: var(--button-interaction-border, var(--button-border));
  background-color: var(--button-interaction-background, var(--button-background));
  color: var(--button-interaction-color, var(--button-color));
  box-shadow: var(--button-interaction-box-shadow, var(--button-box-shadow));
  text-decoration: var(--button-interaction-text-decoration, var(--button-text-decoration));
}

.base-button:where(:not(:disabled):not([aria-disabled="true"])):hover {
  --button-interaction-border: var(--button-hover-border);
  --button-interaction-background: var(--button-hover-background);
  --button-interaction-color: var(--button-hover-color);
  --button-interaction-box-shadow: var(--button-hover-box-shadow);
  --button-interaction-text-decoration: var(--button-hover-text-decoration);
}

.base-button:where(:focus-visible) {
  --button-interaction-border: var(--button-focus-border);
  --button-interaction-background: var(--button-focus-background);
  --button-interaction-color: var(--button-focus-color);
  --button-interaction-box-shadow: var(--button-focus-box-shadow);
  --button-interaction-text-decoration: var(--button-focus-text-decoration);
  outline: 2px solid var(--button-focus-outline, var(--color-nblue-8));
  outline-offset: 4px;
}

.base-button:where(:not(:disabled):not([aria-disabled="true"])):active {
  --button-interaction-border: var(--button-active-border);
  --button-interaction-background: var(--button-active-background);
  --button-interaction-color: var(--button-active-color);
  --button-interaction-box-shadow: var(--button-active-box-shadow);
  --button-interaction-text-decoration: var(--button-active-text-decoration);
  transform: var(--button-active-transform);
}

.base-button:where(:disabled) {
  border-color: var(--button-disabled-border);
  background-color: var(--button-disabled-background);
  color: var(--button-disabled-color);
  box-shadow: var(--button-disabled-box-shadow);
  text-decoration: var(--button-disabled-text-decoration);
  transform: var(--button-disabled-transform);
  cursor: not-allowed;
}

.base-button[aria-disabled="true"]:not(:disabled) {
  border-color: var(--button-inactive-border);
  background-color: var(--button-inactive-background);
  color: var(--button-inactive-color);
  box-shadow: var(--button-inactive-box-shadow);
  text-decoration: var(--button-inactive-text-decoration);
  transform: var(--button-inactive-transform);
  cursor: not-allowed;
  /*pointer-events: none;*/
}

.base-button:is(:disabled, [aria-disabled="true"]) {
  --button-interaction-border: initial;
  --button-interaction-background: initial;
  --button-interaction-color: initial;
  --button-interaction-box-shadow: initial;
  --button-interaction-text-decoration: initial;
}

.button-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.button-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
